export const CLOZE_POINTS_FIRST_TRY = 4;
export const CLOZE_POINTS_AFTER_MISS = 2;

const MIN_WORDS_FOR_CLOZE = 3;
const MIN_ANSWER_LENGTH = 3;
const MAX_DISTRACTORS = 3;
const EDGE_PUNCTUATION =
  /^[.,;:!?¡¿"'«»“”‘’()[\]{}…\-–—、。「」！？]+|[.,;:!?¡¿"'«»“”‘’()[\]{}…\-–—、。「」！？]+$/g;

export const splitIntoWordTokens = (text) =>
  text
    .split(/(\s+)/)
    .filter(Boolean)
    .map((token) => {
      if (/^\s+$/.test(token)) return { text: token, word: null };
      return { text: token, word: token.replace(EDGE_PUNCTUATION, "") || null };
    });

const findClozeTokenIndex = (tokens) => {
  if (tokens.filter((token) => token.word).length < MIN_WORDS_FOR_CLOZE) return null;
  let bestIndex = null;
  let bestLength = MIN_ANSWER_LENGTH - 1;
  tokens.forEach((token, index) => {
    if (!token.word || /\d/.test(token.word)) return;
    const length = [...token.word].length;
    if (length > bestLength) {
      bestIndex = index;
      bestLength = length;
    }
  });
  return bestIndex;
};

const joinTokens = (tokens) => tokens.map((token) => token.text).join("");

const buildCloze = (text) => {
  const tokens = splitIntoWordTokens(text);
  const index = findClozeTokenIndex(tokens);
  if (index === null) return null;
  const token = tokens[index];
  const answer = token.word;
  const answerStart = token.text.indexOf(answer);
  return {
    after: token.text.slice(answerStart + answer.length) + joinTokens(tokens.slice(index + 1)),
    answer,
    before: joinTokens(tokens.slice(0, index)) + token.text.slice(0, answerStart)
  };
};

const seededRandom = (seed) => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffle = (items, random) => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
};

const isCapitalized = (word) => word.charAt(0) !== word.charAt(0).toLowerCase();

const matchCapitalization = (option, answer) => {
  const first = option.charAt(0);
  return (isCapitalized(answer) ? first.toUpperCase() : first.toLowerCase()) + option.slice(1);
};

const buildOptions = (answer, allAnswers, seed) => {
  const random = seededRandom(seed);
  const seen = new Set([answer.toLowerCase()]);
  const pool = allAnswers.filter((candidate) => {
    const lowered = candidate.toLowerCase();
    if (seen.has(lowered)) return false;
    seen.add(lowered);
    return true;
  });
  const distractors = shuffle(pool, random)
    .slice(0, MAX_DISTRACTORS)
    .map((distractor) => matchCapitalization(distractor, answer));
  return shuffle([answer, ...distractors], random);
};

export const buildClozeStory = (paragraphs) => {
  const withoutOptions = paragraphs.map((paragraph, paragraphIndex) =>
    paragraph.sentences.map((sentence, sentenceIndex) => ({
      cloze: sentence.cloze === undefined ? buildCloze(sentence.text) : null,
      key: `${paragraphIndex}-${sentenceIndex}`,
      sentence
    }))
  );
  const allAnswers = withoutOptions.flat().flatMap((item) => (item.cloze ? [item.cloze.answer] : []));
  return withoutOptions.map((paragraph) =>
    paragraph.map((item) => ({
      ...item,
      cloze:
        item.sentence.cloze === undefined
          ? item.cloze && { ...item.cloze, options: buildOptions(item.cloze.answer, allAnswers, item.sentence.id) }
          : item.sentence.cloze
    }))
  );
};

export const findNextUnansweredKey = (keys, currentKey, answeredKeys) => {
  const start = keys.indexOf(currentKey);
  for (let offset = 1; offset <= keys.length; offset++) {
    const key = keys[(start + offset) % keys.length];
    if (!answeredKeys.has(key)) return key;
  }
  return null;
};
